import { useEffect, useState, type FC, type FormEvent } from "react";
import { Button, Modal } from "../../_common";
import { useSetTransactionBlockedQuantity } from "../portfolio.hooks";
import type { Transaction } from "../portfolio.types";

export interface TransactionBlockModalProps {
  portfolioId: string;
  transaction: Transaction | null;
  onClose: () => void;
  onSaved: (transaction: Transaction) => void;
}

const inputClass = "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";

export const TransactionBlockModal: FC<TransactionBlockModalProps> = ({
  portfolioId,
  transaction,
  onClose,
  onSaved,
}) => {
  const mutation = useSetTransactionBlockedQuantity();
  const [quantity, setQuantity] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!transaction) return;
    const previouslyBlocked = transaction.blockedQuantity ?? 0;
    setQuantity(String(previouslyBlocked > 0 ? previouslyBlocked : transaction.openQuantity ?? transaction.quantity ?? 0));
    setValidationError(null);
    mutation.reset();
  }, [transaction, mutation.reset]);

  if (!transaction) return null;

  const remainingQuantity = transaction.openQuantity ?? transaction.quantity ?? 0;
  const blockedQuantity = Number(quantity);
  const save = async (nextQuantity: number) => {
    if (!Number.isFinite(nextQuantity) || nextQuantity < 0 || nextQuantity > remainingQuantity) {
        setValidationError(`Enter a quantity from 0 to ${remainingQuantity}.`);
      return;
    }
    setValidationError(null);
    try {
      const updatedTransaction = await mutation.mutateAsync({
        id: transaction.id!,
        portfolioId,
        blockedQuantity: nextQuantity,
      });
      onSaved(updatedTransaction);
      onClose();
    } catch {
      // The mutation error is shown below.
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void save(blockedQuantity);
  };

  return (
    <Modal open onClose={onClose} title="Mark purchase as blocked" maxWidth="max-w-md">
      <p className="mb-3 text-sm text-muted-foreground">
        {transaction.symbolTicker ?? "Security"} · {transaction.date ?? "—"} · {transaction.accountName ?? "Account"}
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <label htmlFor="blocked-quantity" className="text-sm font-medium">
          Blocked quantity (remaining from purchase: {remainingQuantity})
        </label>
        <input
          id="blocked-quantity"
          className={inputClass}
          type="number"
          min="0"
          max={remainingQuantity}
          step="any"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          required
        />
        <p className="text-xs text-muted-foreground">
          Set 0 to remove the mark. This does not change the recorded purchase or portfolio value.
        </p>
        {(validationError || mutation.error) && (
          <p role="alert" className="text-sm text-destructive">
            {validationError ?? mutation.error?.message}
          </p>
        )}
        <div className="mt-2 flex justify-between gap-2">
          <div>
            {(transaction.blockedQuantity ?? 0) > 0 && (
              <Button type="button" variant="outline" disabled={mutation.isPending} onClick={() => void save(0)}>
                Unblock
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" disabled={mutation.isPending} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
